import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { queryKeys } from '../hooks/data/queryKeys'
import getBlockedMonitoringPeriods from '../services/ruutsApi/getBlockedMonitoringPeriods'
import { ProgramConfig } from '../types/apiModels'
import AppContext from './appContext'

type MonitoringPeriodContextType = {
  checkForBlockedMonitoringPeriods: ({
    blockedMonitoringPeriods,
    farmId,
  }: {
    blockedMonitoringPeriods?: ProgramConfig['blockedMonitoringPeriods']
    farmId?: string
  }) => Promise<void>
  hasBlockedMonitoringPeriods: boolean
}

const MonitoringPeriodContext = createContext<MonitoringPeriodContextType>({
  checkForBlockedMonitoringPeriods: async () => {},
  hasBlockedMonitoringPeriods: false,
})

export const useMonitoringPeriodContext = () => {
  const context = useContext(MonitoringPeriodContext)
  if (!context) {
    throw new Error('useMonitoringPeriodContext must be used within a MonitoringPeriodProvider')
  }
  return context
}

type CheckParams = {
  farmId?: string
  hasBlockedConfig: boolean
}

export const MonitoringPeriodProvider = ({ children }: { children: React.ReactNode }) => {
  const { programConfig, currentFarm } = useContext(AppContext)
  const queryClient = useQueryClient()
  const [checkParams, setCheckParams] = useState<CheckParams | null>(null)

  const configuredBlockedPeriods = programConfig?.blockedMonitoringPeriods as
    | ProgramConfig['blockedMonitoringPeriods']
    | undefined

  // An explicit imperative check (FarmSelector/NavBar) pins the farm being evaluated;
  // otherwise fall back to the currently selected farm and its program config.
  const farmId = checkParams?.farmId ?? currentFarm?.id
  const hasBlockedConfig = checkParams
    ? checkParams.hasBlockedConfig
    : !!configuredBlockedPeriods?.length

  const { data, isError } = useQuery({
    queryKey: queryKeys.blockedMonitoringPeriods(farmId),
    queryFn: ({ signal }) => getBlockedMonitoringPeriods({ farmId: farmId as string, signal }),
    enabled: !!farmId && hasBlockedConfig,
  })

  // Fail-closed: any error means we must assume the farm is inside a blocked monitoring
  // period (this gates GIS-Technician edit access in useUserRole). Only a successful, empty
  // result unblocks. Reads pagination.total from the migrated unwrapPaginated shape — NOT the
  // pre-migration `records` field, which would silently evaluate to 0 (fail-OPEN) post-migration.
  const hasBlockedMonitoringPeriods = isError ? true : (data?.pagination?.total ?? 0) > 0

  const checkForBlockedMonitoringPeriods = useCallback(
    async ({
      blockedMonitoringPeriods,
      farmId: farmIdArg,
    }: {
      blockedMonitoringPeriods?: ProgramConfig['blockedMonitoringPeriods']
      farmId?: string
    }) => {
      const effectiveBlocked = blockedMonitoringPeriods || configuredBlockedPeriods
      const selectedFarmId = farmIdArg || currentFarm?.id
      const configPresent = !!effectiveBlocked?.length

      setCheckParams({ farmId: selectedFarmId, hasBlockedConfig: configPresent })

      if (!selectedFarmId || !configPresent) return

      try {
        // Warm the cache so the fail-closed value is settled before callers navigate on.
        // Errors are swallowed here on purpose — the reactive query's isError above is what
        // drives the fail-closed derivation, and propagating would turn a farm-selection
        // flow into a hard error instead of a safe default.
        await queryClient.ensureQueryData({
          queryKey: queryKeys.blockedMonitoringPeriods(selectedFarmId),
          queryFn: ({ signal }) => getBlockedMonitoringPeriods({ farmId: selectedFarmId, signal }),
        })
      } catch {
        // no-op — fail-closed is handled by the reactive query state
      }
    },
    [configuredBlockedPeriods, currentFarm, queryClient],
  )

  const contextValue = useMemo(
    () => ({
      checkForBlockedMonitoringPeriods,
      hasBlockedMonitoringPeriods,
    }),
    [checkForBlockedMonitoringPeriods, hasBlockedMonitoringPeriods],
  )

  return (
    <MonitoringPeriodContext.Provider value={contextValue}>
      {children}
    </MonitoringPeriodContext.Provider>
  )
}
